import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useBranding } from '@/App';
import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LayoutDashboard, Wallet, FileText, CreditCard, Key,
  Bell, Plug, Settings, LogOut, FlaskConical, AlertTriangle,
  Sun, Moon, ChevronDown, ChevronRight, Download, MessageSquare, BookOpen, DollarSign
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function BalanceChip() {
  const [balance, setBalance] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/billing/balance`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}` }
    }).then(r => setBalance(r.data?.balance ?? null)).catch(() => {});
  }, []);

  if (balance === null) return null;

  const low = balance < 10 && balance > 0;
  const empty = balance <= 0;
  const color = empty ? 'text-red-400 bg-red-500/10 border-red-500/20'
    : low ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
    : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';

  return (
    <button
      data-testid="balance-chip"
      onClick={() => navigate('/billing')}
      title="Account Balance — click to manage"
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all hover:scale-105 ${color}`}
    >
      <DollarSign className="h-3.5 w-3.5" />
      {parseFloat(balance).toFixed(2)} USDT
    </button>
  );
}

const menuSections = [
  {
    label: 'MAIN',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
      { icon: Wallet, label: 'Wallets', path: '/wallets' },
      { icon: FileText, label: 'Invoices', path: '/invoices' },
    ],
  },
  {
    label: 'PAYMENTS',
    items: [
      { icon: CreditCard, label: 'Payments', path: '/payments' },
      { icon: Key, label: 'API Keys', path: '/api-keys' },
    ],
  },
  {
    label: 'INTEGRATIONS',
    items: [
      { icon: Bell, label: 'Notifications', path: '/webhooks' },
      { icon: Plug, label: 'Integrations', path: '/integrations' },
      { icon: Download, label: 'Downloads', path: '/demo-downloads' },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { icon: CreditCard, label: 'Billing', path: '/billing' },
      { icon: MessageSquare, label: 'Support', path: '/support' },
      { icon: BookOpen, label: 'Knowledge Base', path: '/knowledge-base' },
      { icon: Settings, label: 'Settings', path: '/settings' },
    ],
  },
];

const MerchantLayout = ({ children, title }) => {
  const { user, logout, toggleTestMode } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;
  const location = useLocation();
  const navigate = useNavigate();
  const [toggling, setToggling] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [paymentNotifs, setPaymentNotifs] = useState([]);
  const [paymentUnread, setPaymentUnread] = useState(0);

  const MERCHANT_READ_KEY = 'merchant_payment_notif_read_at';

  const isTestMode = user?.test_mode !== false;
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  const fetchPaymentNotifs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payments/notifications?limit=15`);
      const notifs = data.notifications || [];
      setPaymentNotifs(notifs);
      const lastRead = localStorage.getItem(MERCHANT_READ_KEY);
      const unread = lastRead
        ? notifs.filter(n => (n.paid_at || n.created_at) > lastRead).length
        : notifs.length;
      setPaymentUnread(unread);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchPaymentNotifs();
    const interval = setInterval(fetchPaymentNotifs, 30000);
    return () => clearInterval(interval);
  }, [fetchPaymentNotifs]);

  useEffect(() => {
    document.title = title ? `${title} | CoinPayUp` : 'CoinPayUp | Crypto Payment Gateway';
  }, [title]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/invoices?status=pending&per_page=1`);
        const pending = data.summary?.pending?.count || 0;
        const overdue = data.summary?.overdue?.count || 0;
        setPendingInvoiceCount(pending + overdue);
      } catch {}
    };
    fetchPendingCount();
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  const handleToggleTestMode = async () => {
    setToggling(true);
    try {
      const result = await toggleTestMode();
      toast.success(result.message);
    } catch (error) {
      toast.error('Failed to toggle test mode');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="merchant-layout">
      {/* Sidebar */}
      <aside className="w-[240px] bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))] hidden lg:flex flex-col shrink-0">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 gap-2.5">
          {activeLogo ? (
            <img src={activeLogo} alt={brand_name} className="h-7 max-w-[110px] object-contain" />
          ) : (
            <>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <div>
                <span className="font-bold text-[15px] text-foreground tracking-tight">{brand_name || 'CoinPayUp'}</span>
                <p className="text-[10px] text-[hsl(var(--sidebar-section))] leading-none mt-0.5">Payment Gateway</p>
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 pt-4 pb-3 overflow-y-auto">
          {menuSections.map((section) => (
            <div key={section.label} className="mb-5">
              <p className="text-[10px] font-semibold text-[hsl(var(--sidebar-section))] uppercase tracking-[0.12em] px-3 mb-2">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-[hsl(var(--sidebar-active-bg))] text-white shadow-md shadow-indigo-500/20'
                          : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-foreground'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2 : 1.5} />
                      <span className="flex-1">{item.label}</span>
                      {item.label === 'Billing' && pendingInvoiceCount > 0 && (
                        <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none px-1" data-testid="billing-pending-badge">
                          {pendingInvoiceCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Test Banner */}
        {isTestMode && (
          <div className="bg-amber-500/90 text-black px-4 py-1.5 flex items-center justify-center gap-2 text-[12px] font-semibold" data-testid="test-mode-banner">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>TEST MODE — Transactions are simulated and no real payments are processed</span>
          </div>
        )}

        {/* Top Bar */}
        <header className="h-14 bg-[hsl(var(--topbar-bg))] border-b border-[hsl(var(--sidebar-border))] px-6 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3 flex-1"></div>

          <div className="flex items-center gap-2">
            {/* Test mode toggle */}
            <div className="flex items-center gap-2 mr-2" data-testid="test-mode-toggle-container">
              <FlaskConical className={`h-3.5 w-3.5 ${isTestMode ? 'text-amber-500' : 'text-muted-foreground'}`} />
              <span className={`text-[12px] font-semibold ${isTestMode ? 'text-amber-500' : 'text-muted-foreground'}`}>
                {isTestMode ? 'Test' : 'Live'}
              </span>
              <Switch
                checked={isTestMode}
                onCheckedChange={handleToggleTestMode}
                disabled={toggling}
                data-testid="test-mode-toggle"
                className={isTestMode ? 'data-[state=checked]:bg-amber-500' : ''}
              />
            </div>

            <div className="h-6 w-px bg-border" />

            {/* Account Balance Chip */}
            <BalanceChip />

            {/* Payment Notifications Bell */}
            <div className="relative" data-testid="merchant-payment-notifications">
              <button
                onClick={() => {
                  setBellOpen(!bellOpen);
                  if (!bellOpen) {
                    localStorage.setItem(MERCHANT_READ_KEY, new Date().toISOString());
                    setPaymentUnread(0);
                  }
                }}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[hsl(var(--surface-hover))] transition-colors text-muted-foreground hover:text-foreground relative"
                data-testid="notifications-bell"
              >
                <Bell className="h-4 w-4" />
                {paymentUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white bg-emerald-500 flex items-center justify-center" data-testid="merchant-notif-badge">
                    {paymentUnread > 99 ? '99+' : paymentUnread}
                  </span>
                )}
              </button>
              {bellOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="merchant-notifs-dropdown">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">Payments Received</span>
                      {paymentNotifs.length > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500">
                          {paymentNotifs.length} total
                        </span>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {paymentNotifs.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-40" />
                          <p className="text-sm text-muted-foreground">No payments received yet</p>
                        </div>
                      ) : (
                        paymentNotifs.map((n, i) => (
                          <div key={i} className="px-4 py-3 border-b border-gray-200 dark:border-white/10 last:border-0 hover:bg-muted/50 transition-colors">
                            <div className="flex items-start gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground">
                                  {n.received_amount || n.amount} {n.currency}
                                  {n.status === 'overpaid' && <span className="ml-1 text-[10px] bg-amber-500/10 text-amber-500 px-1 rounded">overpaid</span>}
                                </p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                  {n.network?.toUpperCase()} &bull; {n.paid_at ? new Date(n.paid_at).toLocaleString() : '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="px-4 py-2.5 border-t border-gray-200 dark:border-white/10">
                      <button
                        onClick={() => { setBellOpen(false); navigate('/payments'); }}
                        className="w-full text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        data-testid="view-all-merchant-payments-btn"
                      >
                        View All Payments
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[hsl(var(--surface-hover))] transition-colors text-muted-foreground hover:text-foreground"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* User avatar dropdown */}
            <div className="relative ml-1" data-testid="user-menu">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 cursor-pointer"
                data-testid="user-avatar-btn"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                  <span className="text-white text-[11px] font-bold">{initials}</span>
                </div>
                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-gray-200 dark:border-white/10 rounded-xl shadow-lg z-50 py-1" data-testid="user-dropdown">
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[13px] text-foreground hover:bg-muted transition-colors"
                      data-testid="dropdown-settings"
                    >
                      <Settings className="h-4 w-4 text-muted-foreground" />
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[13px] text-red-500 hover:bg-muted transition-colors"
                      data-testid="dropdown-logout"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Log out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Header */}
        <div className="px-6 pt-6 pb-4">
          <h1 className="text-2xl font-bold text-foreground tracking-tight" data-testid="page-title">{title}</h1>
        </div>

        {/* Page Content */}
        <div className="flex-1 px-6 pb-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MerchantLayout;
